import { useState } from "react";
import {
    GlobalBookingGuestPickerGuestsWrapper,
    GlobalBookingGuestPickerGuestsTitleWrapper,
    GlobalBookingGuestPickerGuestsTitle,
    GlobalBookingGuestPickerGuestsTitleTextWrapper,
    GlobalBookingGuestPickerGuestsSubtitle,
    GlobalBookingGuestPickerRow,
    GlobalBookingGuestPickerRowTextTitle,
    GlobalBookingGuestPickerRowTextSubtitle,
    GlobalBookingGuestPickerRowTextWrapper,
    GlobalBookingGuestPickerRowTextNotAllowed,
    GlobalBookingGuestPickerButtonWrapper,
    GlobalBookingGuestPickerButton,
} from "./GlobalBookingGuestPickerStyling";
import useClickOutside from "../../hook/useClickOutside";
import { UserModel } from "../../interfaces/models/userModel";
import { ListingModel } from "../../interfaces/models/listingModel";
import { UnifiedListingModel } from "../../interfaces/models/UnifiedListingModel";
import GlobalPopupContainerCenter from "../globalPopup/GlobalPopupContainerCenter";

interface GlobalBookingGuestPickerProps {
    adults: number;
    children: number;
    infants: number;
    pets: number;
    listingOwnerData: UserModel | null;
    listing: UnifiedListingModel;
    toggleGuests: () => void;
    updateGuests: (guests: { adults: number; children: number; infants: number; pets: number }) => Promise<void>;
}

export default function GlobalBookingGuestPicker({ adults, children, infants, pets, listingOwnerData, listing, toggleGuests, updateGuests }: GlobalBookingGuestPickerProps) {
    const [adultsNum, setAdultsNum] = useState(adults);
    const [childrenNum, setChildrenNum] = useState(children);
    const [infantsNum, setInfantsNum] = useState(infants);
    const [petsNum, setPetsNum] = useState(pets);
    const [isContinueBtnLoading, setIsContinueBtnLoading] = useState(false);
    const wrapperRef = useClickOutside(true, () => {
        if (!isContinueBtnLoading) {
            toggleGuests();
        }
    });

    const resetGuests = () => {
        setAdultsNum(1);
        setChildrenNum(0);
        setInfantsNum(0);
        setPetsNum(0);
    };

    const onSubmit = async () => {
        setIsContinueBtnLoading(true);
        await updateGuests({
            adults: adultsNum,
            children: childrenNum,
            infants: infantsNum,
            pets: petsNum,
        });
        toggleGuests();
    };

    return (
        <GlobalPopupContainerCenter
            style={{ 
                maxWidth: "410px",
                width: "100%", 
                borderRadius: "32px",
                overflow: "hidden"
            }}
            ref={wrapperRef}
            closePopup={toggleGuests}
            handleSave={onSubmit}
            disable={isContinueBtnLoading}
            isContinueBtnLoading={isContinueBtnLoading}
            noBackBtn
            cancelBtnText="Clear"
            cancelPopup={resetGuests}
            desktopWidth="410px"
        >
            <GlobalBookingGuestPickerGuestsWrapper>
                <GlobalBookingGuestPickerGuestsTitleWrapper>
                    <GlobalBookingGuestPickerGuestsTitleTextWrapper>
                        <GlobalBookingGuestPickerGuestsTitle>Who's coming?</GlobalBookingGuestPickerGuestsTitle>
                        <GlobalBookingGuestPickerGuestsSubtitle>
                        {listing.guests >= 0
                                ? `This place sleeps up to ${listing.guests} adults.`
                                : `This place sleeps up to 1 adult.`}
                        </GlobalBookingGuestPickerGuestsSubtitle>
                    </GlobalBookingGuestPickerGuestsTitleTextWrapper>
                </GlobalBookingGuestPickerGuestsTitleWrapper>
                <GlobalBookingGuestPickerRow>
                    <GlobalBookingGuestPickerRowTextWrapper>
                        <GlobalBookingGuestPickerRowTextTitle>Adults</GlobalBookingGuestPickerRowTextTitle>
                        <GlobalBookingGuestPickerRowTextSubtitle>Ages 18 or over</GlobalBookingGuestPickerRowTextSubtitle>
                    </GlobalBookingGuestPickerRowTextWrapper>
                    <GlobalBookingGuestPickerButtonWrapper>
                        <GlobalBookingGuestPickerButton
                            onClick={() => {
                                if (adultsNum > 1) {
                                    setAdultsNum((prevState) => prevState - 1);
                                }
                            }}
                            disabled={adultsNum <= 1}
                        >
                            <svg 
                                viewBox="0 0 12 12" 
                                xmlns="http://www.w3.org/2000/svg" 
                                aria-hidden="true" 
                                role="presentation" 
                                focusable="false" 
                                style={{ display: "block", height: "12px", width: "12px", fill: "currentcolor" }}
                            >
                                <path d="m.75 6.75h10.5v-1.5h-10.5z"></path>
                            </svg>
                        </GlobalBookingGuestPickerButton>
                        <GlobalBookingGuestPickerRowTextTitle>{adultsNum}</GlobalBookingGuestPickerRowTextTitle>
                        <GlobalBookingGuestPickerButton
                            onClick={() => {
                                if (adultsNum + childrenNum < listing.guests) {
                                    setAdultsNum((prevState) => prevState + 1);
                                }
                            }}
                            disabled={adultsNum + childrenNum >= listing.guests}
                        >
                            <svg 
                                viewBox="0 0 12 12" 
                                xmlns="http://www.w3.org/2000/svg" 
                                aria-hidden="true" 
                                role="presentation" 
                                focusable="false" 
                                style={{ display: "block", height: "12px", width: "12px", fill: "currentcolor" }}
                            >
                                <path d="m6.75.75v4.5h4.5v1.5h-4.5v4.5h-1.5v-4.5h-4.5v-1.5h4.5v-4.5z"></path>
                            </svg>
                        </GlobalBookingGuestPickerButton>
                    </GlobalBookingGuestPickerButtonWrapper>
                </GlobalBookingGuestPickerRow>
                <GlobalBookingGuestPickerRow>
                    <GlobalBookingGuestPickerRowTextWrapper>
                        <GlobalBookingGuestPickerRowTextTitle>Children</GlobalBookingGuestPickerRowTextTitle>
                        <GlobalBookingGuestPickerRowTextSubtitle>Ages 2-17</GlobalBookingGuestPickerRowTextSubtitle>
                    </GlobalBookingGuestPickerRowTextWrapper>
                    {listing.houseRules.includes("No children") ? (
                        <GlobalBookingGuestPickerRowTextNotAllowed>Not allowed</GlobalBookingGuestPickerRowTextNotAllowed>
                    ) : (
                        <GlobalBookingGuestPickerButtonWrapper>
                            <GlobalBookingGuestPickerButton
                                onClick={() => {
                                    if (childrenNum > 0) {
                                        setChildrenNum((prevState) => prevState - 1);
                                    }
                                }}
                                disabled={childrenNum <= 0}
                            >
                                <svg 
                                    viewBox="0 0 12 12" 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    aria-hidden="true" 
                                    role="presentation" 
                                    focusable="false" 
                                    style={{ display: "block", height: "12px", width: "12px", fill: "currentcolor" }}
                                >
                                    <path d="m.75 6.75h10.5v-1.5h-10.5z"></path>
                                </svg>
                            </GlobalBookingGuestPickerButton>
                            <GlobalBookingGuestPickerRowTextTitle>{childrenNum}</GlobalBookingGuestPickerRowTextTitle>
                            <GlobalBookingGuestPickerButton
                                onClick={() => {
                                    if (adultsNum + childrenNum < listing.guests) {
                                        setChildrenNum((prevState) => prevState + 1);
                                    }
                                }}
                                disabled={adultsNum + childrenNum >= listing.guests}
                            >
                                <svg 
                                    viewBox="0 0 12 12" 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    aria-hidden="true" 
                                    role="presentation" 
                                    focusable="false" 
                                    style={{ display: "block", height: "12px", width: "12px", fill: "currentcolor" }}
                                >
                                    <path d="m6.75.75v4.5h4.5v1.5h-4.5v4.5h-1.5v-4.5h-4.5v-1.5h4.5v-4.5z"></path>
                                </svg>
                            </GlobalBookingGuestPickerButton>
                        </GlobalBookingGuestPickerButtonWrapper>
                    )}
                </GlobalBookingGuestPickerRow>
                <GlobalBookingGuestPickerRow>
                    <GlobalBookingGuestPickerRowTextWrapper>
                        <GlobalBookingGuestPickerRowTextTitle>Infants</GlobalBookingGuestPickerRowTextTitle>
                        <GlobalBookingGuestPickerRowTextSubtitle>Under 2</GlobalBookingGuestPickerRowTextSubtitle>
                    </GlobalBookingGuestPickerRowTextWrapper>
                    {listing.houseRules.includes("No infants") ? (
                        <GlobalBookingGuestPickerRowTextNotAllowed>Not allowed</GlobalBookingGuestPickerRowTextNotAllowed>
                    ) : (
                        <GlobalBookingGuestPickerButtonWrapper>
                            <GlobalBookingGuestPickerButton
                                onClick={() => {
                                    if (infantsNum > 0) {
                                        setInfantsNum((prevState) => prevState - 1);
                                    }
                                }}
                                disabled={infantsNum <= 0}
                            >
                                <svg 
                                    viewBox="0 0 12 12" 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    aria-hidden="true" 
                                    role="presentation" 
                                    focusable="false" 
                                    style={{ display: "block", height: "12px", width: "12px", fill: "currentcolor" }}
                                >
                                    <path d="m.75 6.75h10.5v-1.5h-10.5z"></path>
                                </svg>
                            </GlobalBookingGuestPickerButton>
                            <GlobalBookingGuestPickerRowTextTitle>{infantsNum}</GlobalBookingGuestPickerRowTextTitle>
                            <GlobalBookingGuestPickerButton
                                onClick={() => {
                                    if (infantsNum < 99) {
                                        setInfantsNum((prevState) => prevState + 1);
                                    }
                                }}
                                disabled={infantsNum >= 99}
                            >
                                <svg 
                                    viewBox="0 0 12 12" 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    aria-hidden="true" 
                                    role="presentation" 
                                    focusable="false" 
                                    style={{ display: "block", height: "12px", width: "12px", fill: "currentcolor" }}
                                >
                                    <path d="m6.75.75v4.5h4.5v1.5h-4.5v4.5h-1.5v-4.5h-4.5v-1.5h4.5v-4.5z"></path>
                                </svg>
                            </GlobalBookingGuestPickerButton>
                        </GlobalBookingGuestPickerButtonWrapper>
                    )}
                </GlobalBookingGuestPickerRow>
                <GlobalBookingGuestPickerRow>
                    <GlobalBookingGuestPickerRowTextWrapper>
                        <GlobalBookingGuestPickerRowTextTitle>Pets</GlobalBookingGuestPickerRowTextTitle>
                        <GlobalBookingGuestPickerRowTextSubtitle>Friendly animals</GlobalBookingGuestPickerRowTextSubtitle>
                    </GlobalBookingGuestPickerRowTextWrapper>
                    {listing.houseRules.includes("No pets") ? (
                        <GlobalBookingGuestPickerRowTextNotAllowed>Not allowed</GlobalBookingGuestPickerRowTextNotAllowed>
                    ) : (
                        <GlobalBookingGuestPickerButtonWrapper>
                            <GlobalBookingGuestPickerButton
                                onClick={() => {
                                    if (petsNum > 0) {
                                        setPetsNum((prevState) => prevState - 1);
                                    }
                                }}
                                disabled={petsNum <= 0}
                            >
                                <svg 
                                    viewBox="0 0 12 12" 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    aria-hidden="true" 
                                    role="presentation" 
                                    focusable="false" 
                                    style={{ display: "block", height: "12px", width: "12px", fill: "currentcolor" }}
                                >
                                    <path d="m.75 6.75h10.5v-1.5h-10.5z"></path>
                                </svg>
                            </GlobalBookingGuestPickerButton>
                            <GlobalBookingGuestPickerRowTextTitle>{petsNum}</GlobalBookingGuestPickerRowTextTitle>
                            <GlobalBookingGuestPickerButton
                                onClick={() => {
                                    if (petsNum < 99) {
                                        setPetsNum((prevState) => prevState + 1);
                                    }
                                }}
                                disabled={petsNum >= 99}
                            >
                                <svg 
                                    viewBox="0 0 12 12" 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    aria-hidden="true" 
                                    role="presentation" 
                                    focusable="false" 
                                    style={{ display: "block", height: "12px", width: "12px", fill: "currentcolor" }}
                                >
                                    <path d="m6.75.75v4.5h4.5v1.5h-4.5v4.5h-1.5v-4.5h-4.5v-1.5h4.5v-4.5z"></path>
                                </svg>
                            </GlobalBookingGuestPickerButton>
                        </GlobalBookingGuestPickerButtonWrapper>
                    )}
                </GlobalBookingGuestPickerRow>
            </GlobalBookingGuestPickerGuestsWrapper>
        </GlobalPopupContainerCenter>
    );
}